// ./src/utils/api/TimelineItemService.ts
//
// Event timeline item service — one line on an event's run-of-show (what happens
// before / during / after, when, and who is responsible). OpResult-native,
// event-scoped: every list is filtered by eventId. Full single-item CRUD plus a
// bulk phase / category / responsible change or remove (array of ids in the
// where clause). See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { listAllPages, ListAllOptions } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Enum-like constants (mirror the eventsmagic-api boundary/timeline.ts)
// =============================================================================

export const TIMELINE_PHASE = {
  BEFORE: "before",
  DURING: "during",
  AFTER: "after",
} as const;

export const TIMELINE_PHASE_VALUES = Object.values(TIMELINE_PHASE);

export const TIMELINE_PARTY_KIND = {
  USER: "user",
  CREW: "crew",
  GUEST: "guest",
  VENDOR: "vendor",
  VENUE: "venue",
} as const;

export const TIMELINE_PARTY_KIND_VALUES = Object.values(TIMELINE_PARTY_KIND);

export const TIMELINE_DESCRIPTION_FORMAT = {
  RICH_TEXT: "rich_text",
  MARKDOWN: "markdown",
} as const;

export const TIMELINE_DESCRIPTION_FORMAT_VALUES = Object.values(TIMELINE_DESCRIPTION_FORMAT);

// =============================================================================
// GraphQL
// =============================================================================

const TIMELINE_ITEM_FIELDS = `
  id
  eventId
  accountId
  categoryId
  name
  description
  descriptionFormat
  phase
  startsAt
  endsAt
  responsibleKind
  responsibleId
  responsibleLabel
  vendorId
  vendorLabel
  venueId
  venueLabel
  orderNo
  status
  rights
  createdAt
  updatedAt
`;

const TIMELINE_ITEM_LIST_QUERY = gql`
  query EventTimelineItemList($filter: EventTimelineItemFilter, $params: PaginationAndSorting) {
    eventTimelineItemList(filter: $filter, params: $params) {
      code
      data { ${TIMELINE_ITEM_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const TIMELINE_ITEM_GET_QUERY = gql`
  query EventTimelineItemGet($id: ID) {
    eventTimelineItemGet(id: $id) {
      code
      data { ${TIMELINE_ITEM_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const TIMELINE_ITEM_GET_MANY_QUERY = gql`
  query EventTimelineItemGetMany($ids: [ID]) {
    eventTimelineItemGetMany(ids: $ids) {
      code
      data { ${TIMELINE_ITEM_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const TIMELINE_ITEM_CREATE_MUTATION = gql`
  mutation EventTimelineItemCreate($params: EventTimelineItemInput) {
    eventTimelineItemCreate(params: $params) {
      code
      data { ${TIMELINE_ITEM_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const TIMELINE_ITEM_UPDATE_MUTATION = gql`
  mutation EventTimelineItemUpdate($where: EventTimelineItemWhereInput, $params: EventTimelineItemInput) {
    eventTimelineItemUpdate(where: $where, params: $params) {
      code
      data { ${TIMELINE_ITEM_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const TIMELINE_ITEM_REMOVE_MUTATION = gql`
  mutation EventTimelineItemRemove($where: EventTimelineItemWhereInput) {
    eventTimelineItemRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api EventTimelineItem schema)
// =============================================================================

export interface TimelineItem {
  id?: string;
  eventId?: string | null;
  accountId?: string | null;
  categoryId?: string | null;
  name?: string | null;
  description?: string | null;
  descriptionFormat?: string | null;
  phase?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  responsibleKind?: string | null;
  responsibleId?: string | null;
  responsibleLabel?: string | null;
  vendorId?: string | null;
  vendorLabel?: string | null;
  venueId?: string | null;
  venueLabel?: string | null;
  orderNo?: number | null;
  status?: number;
  /** Actions the current caller may perform ('edit' | 'remove' | 'duplicate'). */
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface TimelineItemInput {
  eventId?: string | null;
  categoryId?: string | null;
  name?: string | null;
  description?: string | null;
  descriptionFormat?: string | null;
  phase?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  responsibleKind?: string | null;
  responsibleId?: string | null;
  vendorId?: string | null;
  venueId?: string | null;
  orderNo?: number | null;
  status?: number | null;
}

// A bulk update (>1 id) may mass-set phase, category or the responsible party.
export interface TimelineItemBulkInput {
  categoryId?: string | null;
  phase?: string | null;
  responsibleKind?: string | null;
  responsibleId?: string | null;
}

export interface TimelineItemFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  categoryId?: string[] | null;
  phase?: string[] | null;
  responsibleKind?: string[] | null;
  responsibleId?: string[] | null;
  vendorId?: string[] | null;
  venueId?: string[] | null;
  status?: number[] | null;
  searchKeyword?: string | null;
}

export interface TimelineItemWhereInput {
  id?: string | string[];
}

// =============================================================================
// Service
// =============================================================================

export class TimelineItemService {
  static async list(
    filter?: TimelineItemFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(TIMELINE_ITEM_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /**
   * Every item matching `filter`, paged — for the RUN SHEET, which needs the whole day.
   * ⚠ Never pass a literal `pageSize` in a print page instead of this: paper has no Load-more
   * button, so a capped sheet is indistinguishable from a complete one. See `listAllPages`.
   */
  static async listAll(filter?: TimelineItemFilter, sortBy?: SortItem[], opts?: ListAllOptions): Promise<OpResult> {
    return listAllPages<TimelineItem>(TimelineItemService.list, filter, sortBy, opts);
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(TIMELINE_ITEM_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(TIMELINE_ITEM_GET_MANY_QUERY, { ids });
  }

  static async create(params: TimelineItemInput): Promise<OpResult> {
    return ApiService.executeMutation(TIMELINE_ITEM_CREATE_MUTATION, { params });
  }

  static async update(where: TimelineItemWhereInput, params: TimelineItemInput): Promise<OpResult> {
    return ApiService.executeMutation(TIMELINE_ITEM_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(TIMELINE_ITEM_REMOVE_MUTATION, { where: { id } });
  }

  /** Mass-set phase / category / responsible on every selected item in one call. */
  static async bulkUpdate(ids: string[], params: TimelineItemBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(TIMELINE_ITEM_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  /** Remove every selected item in one call. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(TIMELINE_ITEM_REMOVE_MUTATION, { where: { id: ids } });
  }
}
